import React, { useState, useEffect, useCallback, useRef } from 'react';
import { callAPI } from "../helper/apiUtils";
import { $crud } from "../helper/CrudFactory";
import LanguageContext from "../helper/LanguageContext";
import { EN } from "../Languages/EN";
import { SettingsBanner, SettingsRow, SettingsSection } from "./settings/SettingsList";
import {
    isWebPushSupported,
    getNotificationPermission,
    isWebPushDisabledByUser,
    enableWebPush,
    disableWebPush,
    setNotificationHoursPref,
} from "../utils/webPushNotifications";
import './NotificationSettings.css';

// Constants
const DEFAULT_TIMES = {
    startTime: '09:00',
    endTime: '18:00'
};

const DAYS_OF_WEEK = [
    { key: 'mon', translationKey: 'monday' },
    { key: 'tue', translationKey: 'tuesday' },
    { key: 'wed', translationKey: 'wednesday' },
    { key: 'thu', translationKey: 'thursday' },
    { key: 'fri', translationKey: 'friday' },
    { key: 'sat', translationKey: 'saturday' },
    { key: 'sun', translationKey: 'sunday' }
];

const AUTO_SAVE_DEBOUNCE_MS = 400;

/**
 * Notifications page of the personal settings (mobile NotificationSettingsPro), in the
 * design's order: browser permission banner · Push · E-mail (`emailSection`, owned by
 * ProfileSetting) · Notification hours.
 *
 * Notification hours: the backend stores the delivery window (GET / POST notificationPreference
 * startTime / endTime). "Von" (sun) edits the window's start and "Bis" (moon) its end.
 */
function NotificationSettings({
    todoNotificationEnabled = false,
    onToggleTodoNotification,
    emailPushNotificationEnabled = true,
    onToggleEmailPushNotification,
    // Kjuup / Kjuup+: e-mail integration is a Kjuup.io feature, so its push switch is shown off and greyed out.
    emailPushLocked = false,
    emailSection = null,
}) {
    const { strings, isGerm } = React.useContext(LanguageContext);
    const t = (key, fallback) => strings[key] || EN[key] || fallback || key;
    const tr = (key, de, en) => strings[key] || (isGerm ? de : en);
    
    // State management
    const [loading, setLoading] = useState(false);
    const [notificationType, setNotificationType] = useState('everyday');
    const [startTime, setStartTime] = useState(DEFAULT_TIMES.startTime);
    const [endTime, setEndTime] = useState(DEFAULT_TIMES.endTime);
    const [customPreferences, setCustomPreferences] = useState({});
    const [isInitialized, setIsInitialized] = useState(false);
    const [pushSupported] = useState(() => isWebPushSupported());
    const [pushPermission, setPushPermission] = useState(() => getNotificationPermission());
    const [pushEnabled, setPushEnabled] = useState(
        () => isWebPushSupported() && !isWebPushDisabledByUser() && getNotificationPermission() === 'granted'
    );
    const [pushBusy, setPushBusy] = useState(false);
    // Snapshot of the last saved/loaded settings — used to skip no-op auto-saves
    const savedSnapshotRef = useRef(null);
    const saveTimerRef = useRef(null);
    const saveInFlightRef = useRef(false);
    const pendingSaveRef = useRef(null);

    // Serialize only the fields that actually get persisted for the current type
    const serializeSettings = useCallback((type, start, end, custom) => {
        if (type === 'custom') {
            return JSON.stringify({ type, custom });
        }
        return JSON.stringify({ type, start, end });
    }, []);

    // Generate time options (30-minute intervals)
    const timeOptions = React.useMemo(() => {
        const options = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                // Use 24-hour format for all languages (consistent with existing behavior)
                options.push({ value: time, label: time });
            }
        }
        return options;
    }, []);

    // "Bis" is the delivery window's end, which may close at 24:00: Von 00:00 · Bis 24:00 is a
    // full 24 h window (no quiet hours). The backend compares "HH:mm" strings, so "24:00" sorts
    // after every real time and the window covers the whole day.
    const endTimeOptions = React.useMemo(() => [...timeOptions, { value: '24:00', label: '24:00' }], [timeOptions]);

    // Helper functions
    const getDayLabel = useCallback((dayKey) => {
        const day = DAYS_OF_WEEK.find(d => d.key === dayKey);
        return day ? (strings[day.translationKey] || dayKey) : dayKey;
    }, [strings]);

    // Time validation
    // Zero-padded "HH:mm" compares correctly as text and also accepts "24:00" (same as the backend).
    const validateTimeRange = useCallback((start, end) => {
        if (!start || !end) return false;
        return start < end;
    }, []);

    // Initialize default custom preferences
    const getDefaultCustomPreferences = useCallback(() => {
        const defaults = {};
        DAYS_OF_WEEK.forEach(day => {
            defaults[day.key] = { 
                startTime: DEFAULT_TIMES.startTime, 
                endTime: DEFAULT_TIMES.endTime 
            };
        });
        return defaults;
    }, []);

    // Load notification preferences from API
    const loadNotificationPreferences = useCallback(async () => {
        try {
            setLoading(true);
            const response = await callAPI('/notificationPreference', {}, 'GET');
            
            console.log('API Response:', response);
            
            if (response?.data?.status && response.data.data) {
                const preferences = response.data.data;
                
                // Handle array response (get first preference)
                const preference = Array.isArray(preferences) ? preferences[0] : preferences;
                
                if (preference) {
                    console.log('Loaded preference:', preference);
                    setNotificationHoursPref(preference);
                    
                    if (preference.type === 'everyday' || preference.type === 'weekdays') {
                        // Set everyday/weekdays preferences
                        setNotificationType(preference.type);
                        setStartTime(preference.startTime || DEFAULT_TIMES.startTime);
                        setEndTime(preference.endTime || DEFAULT_TIMES.endTime);
                        
                        // Initialize custom preferences with defaults
                        setCustomPreferences(getDefaultCustomPreferences());
                    } else if (preference.type === 'custom' && preference.customPreference) {
                        // Set custom preferences
                        setNotificationType('custom');
                        setStartTime(DEFAULT_TIMES.startTime);
                        setEndTime(DEFAULT_TIMES.endTime);
                        
                        // Merge with defaults for missing days
                        const defaultPrefs = getDefaultCustomPreferences();
                        const mergedPrefs = { ...defaultPrefs, ...preference.customPreference };
                        setCustomPreferences(mergedPrefs);
                    } else {
                        // Fallback to defaults
                        setNotificationType('everyday');
                        setStartTime(DEFAULT_TIMES.startTime);
                        setEndTime(DEFAULT_TIMES.endTime);
                        setCustomPreferences(getDefaultCustomPreferences());
                    }
                } else {
                    setNotificationHoursPref(null);
                    // No preferences found, use defaults
                    setNotificationType('everyday');
                    setStartTime(DEFAULT_TIMES.startTime);
                    setEndTime(DEFAULT_TIMES.endTime);
                    setCustomPreferences(getDefaultCustomPreferences());
                }
            } else {
                setNotificationHoursPref(null);
                // API error or no data, use defaults
                setNotificationType('everyday');
                setStartTime(DEFAULT_TIMES.startTime);
                setEndTime(DEFAULT_TIMES.endTime);
                setCustomPreferences(getDefaultCustomPreferences());
            }
        } catch (error) {
            console.error('Error loading notification preferences:', error);
            // Use defaults on error
            setNotificationType('everyday');
            setStartTime(DEFAULT_TIMES.startTime);
            setEndTime(DEFAULT_TIMES.endTime);
            setCustomPreferences(getDefaultCustomPreferences());
        } finally {
            setLoading(false);
            setIsInitialized(true);
        }
    }, [getDefaultCustomPreferences]);

    // Save notification preferences to API (used by auto-save)
    const saveNotificationPreferences = useCallback(async (type, start, end, custom) => {
        // Validate current settings — skip save (don't toast) if invalid; warnings already shown on change
        if (type === 'everyday' || type === 'weekdays') {
            if (!validateTimeRange(start, end)) {
                return;
            }
        } else if (type === 'custom') {
            for (const [, preference] of Object.entries(custom || {})) {
                if (preference && preference.startTime && preference.endTime) {
                    if (!validateTimeRange(preference.startTime, preference.endTime)) {
                        return;
                    }
                }
            }
        }

        // Skip if nothing actually changed vs last successful save
        const snapshot = savedSnapshotRef.current;
        if (snapshot) {
            const current = serializeSettings(type, start, end, custom);
            const saved = serializeSettings(
                snapshot.notificationType,
                snapshot.startTime,
                snapshot.endTime,
                snapshot.customPreferences
            );
            if (current === saved) return;
        }

        if (saveInFlightRef.current) {
            // Another save is running — remember to retry with latest values when it finishes
            pendingSaveRef.current = { type, start, end, custom };
            return;
        }
        saveInFlightRef.current = true;
        pendingSaveRef.current = null;

        try {
            setLoading(true);

            let payload;
            if (type === 'everyday' || type === 'weekdays') {
                payload = {
                    type,
                    startTime: start,
                    endTime: end
                };
            } else {
                payload = {
                    type: 'custom',
                    customPreference: custom
                };
            }

            const response = await callAPI('/notificationPreference/addUpdate', {}, 'POST', payload);

            if (response?.data?.status) {
                savedSnapshotRef.current = {
                    notificationType: type,
                    startTime: start,
                    endTime: end,
                    customPreferences: custom
                };
                setNotificationHoursPref({
                    type,
                    startTime: start,
                    endTime: end,
                    customPreference: type === 'custom' ? custom : null,
                });
            } else {
                $crud.notify({
                    type: 'error',
                    message: response?.data?.message || strings.notification_settings_error
                });
            }
        } catch (error) {
            console.error('Error saving notification preferences:', error);
            $crud.notify({
                type: 'error',
                message: strings.notification_settings_error
            });
        } finally {
            saveInFlightRef.current = false;
            setLoading(false);
            const pending = pendingSaveRef.current;
            if (pending) {
                pendingSaveRef.current = null;
                // Persist the latest change that arrived while this save was in flight
                saveNotificationPreferences(pending.type, pending.start, pending.end, pending.custom);
            }
        }
    }, [validateTimeRange, serializeSettings, strings]);

    // Update custom preference for a specific day
    const updateCustomPreference = useCallback((day, field, value) => {
        setCustomPreferences(prev => {
            const updated = {
                ...prev,
                [day]: {
                    ...prev[day],
                    [field]: value
                }
            };
            
            // Validate and show warning if invalid
            if (updated[day] && updated[day].startTime && updated[day].endTime) {
                if (!validateTimeRange(updated[day].startTime, updated[day].endTime)) {
                    setTimeout(() => {
                        $crud.notify({
                            type: 'warning',
                            message: `${getDayLabel(day)}: ${strings.start_time_before_end_time}`
                        });
                    }, 100);
                }
            }
            
            return updated;
        });
    }, [validateTimeRange, getDayLabel, strings]);

    // Toggle day enabled/disabled
    const toggleDayEnabled = useCallback((day) => {
        setCustomPreferences(prev => ({
            ...prev,
            [day]: prev[day] ? null : { 
                startTime: DEFAULT_TIMES.startTime, 
                endTime: DEFAULT_TIMES.endTime 
            }
        }));
    }, []);

    // Handle time change with validation
    const handleTimeChange = useCallback((field, value) => {
        if (field === 'startTime') {
            setStartTime(value);
        } else if (field === 'endTime') {
            setEndTime(value);
        }
        
        // Validate and show warning
        if (field === 'startTime' && endTime) {
            if (!validateTimeRange(value, endTime)) {
                setTimeout(() => {
                    $crud.notify({
                        type: 'warning',
                        message: strings.start_time_before_end_time
                    });
                }, 100);
            }
        } else if (field === 'endTime' && startTime) {
            if (!validateTimeRange(startTime, value)) {
                setTimeout(() => {
                    $crud.notify({
                        type: 'warning',
                        message: strings.start_time_before_end_time
                    });
                }, 100);
            }
        }
    }, [startTime, endTime, validateTimeRange, strings]);

    // Load preferences on component mount
    useEffect(() => {
        if (!isInitialized) {
            loadNotificationPreferences();
        }
    }, [isInitialized, loadNotificationPreferences]);

    useEffect(() => {
        if (!pushSupported || typeof navigator === 'undefined' || !navigator.permissions || !navigator.permissions.query) {
            return;
        }
        let permissionStatus;
        navigator.permissions.query({ name: 'notifications' }).then((status) => {
            permissionStatus = status;
            setPushPermission(status.state === 'prompt' ? 'default' : status.state);
            status.onchange = () => {
                const next = status.state === 'prompt' ? 'default' : status.state;
                setPushPermission(next);
                if (next !== 'granted') setPushEnabled(false);
            };
        }).catch(() => {});
        return () => {
            if (permissionStatus) permissionStatus.onchange = null;
        };
    }, [pushSupported]);

    const setDesktopNotifications = async (wantOn) => {
        setPushBusy(true);
        try {
            if (wantOn) {
                const result = await enableWebPush();
                setPushPermission(result.permission || getNotificationPermission());
                setPushEnabled(!!result.ok);
                if (!result.ok && result.permission === 'denied') {
                    $crud.notify({
                        type: 'warning',
                        message: t('browser_notifications_blocked', 'Notifications are blocked in your browser. Allow them for this site in the address bar, then try again.')
                    });
                }
            } else {
                await disableWebPush();
                setPushEnabled(false);
            }
        } catch (error) {
            console.error('Desktop notification toggle failed:', error);
            $crud.notify({
                type: 'error',
                message: t('browser_notifications_error', 'Could not update desktop notifications')
            });
        } finally {
            setPushBusy(false);
        }
    };

    // Once loaded, capture the baseline so auto-save doesn't fire for the initial values
    useEffect(() => {
        if (isInitialized && savedSnapshotRef.current === null) {
            savedSnapshotRef.current = { notificationType, startTime, endTime, customPreferences };
        }
    }, [isInitialized, notificationType, startTime, endTime, customPreferences]);

    // Auto-save whenever the user changes type / times / custom day settings
    useEffect(() => {
        if (!isInitialized || savedSnapshotRef.current === null) return;

        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }

        saveTimerRef.current = setTimeout(() => {
            saveNotificationPreferences(notificationType, startTime, endTime, customPreferences);
        }, AUTO_SAVE_DEBOUNCE_MS);

        return () => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }
        };
    }, [isInitialized, notificationType, startTime, endTime, customPreferences, saveNotificationPreferences]);

    // ---- Render (mobile NotificationSettingsPro) ----
    const fromLabel = tr('pro_settings_quiet_from', 'Von', 'From');
    const untilLabel = tr('pro_settings_quiet_until', 'Bis', 'Until');
    const offTitle = tr('pro_settings_push_off_browser', 'Push-Benachrichtigungen sind in diesem Browser ausgeschaltet', 'Push notifications are off in this browser');
    const desktopOn = pushEnabled && pushPermission === 'granted';
    const desktopSub = !pushSupported
        ? t('browser_notifications_unsupported', 'Desktop notifications are not supported in this browser.')
        : pushPermission === 'denied'
            ? t('browser_notifications_blocked', 'Notifications are blocked in your browser. Allow them for this site in the address bar, then try again.')
            : t('browser_notifications_desc', 'Get notified of new messages even when Kjuup is in the background.');

    // Banner (mobile: the device permission): allowed, or off with the way back on.
    let permissionBanner;
    if (desktopOn) {
        permissionBanner = (
            <SettingsBanner
                tone="ok"
                icon="check_circle"
                title={tr('pro_settings_push_allowed_browser', 'Push-Benachrichtigungen sind in diesem Browser erlaubt', 'Push notifications are allowed in this browser')}
            />
        );
    } else if (!pushSupported || pushPermission === 'denied' || pushPermission === 'unsupported') {
        permissionBanner = <SettingsBanner tone="err" icon="notifications_off" title={offTitle} body={desktopSub} />;
    } else {
        permissionBanner = (
            <SettingsBanner tone="err" icon="notifications_off" title={offTitle} body={desktopSub}>
                <button
                    type="button"
                    className={`kj-btn kj-btn--primary kj-btn--sm${pushBusy ? ' kj-btn--loading' : ''}`}
                    disabled={pushBusy}
                    onClick={() => setDesktopNotifications(true)}
                >
                    {tr('pro_settings_push_allow_browser', 'Benachrichtigungen erlauben', 'Allow notifications')}
                </button>
            </SettingsBanner>
        );
    }

    const scheduleTypes = [
        { value: 'everyday', icon: 'calendar_month', label: strings.every_day || tr('every_day', 'Jeden Tag', 'Every day') },
        { value: 'weekdays', icon: 'work', label: tr('v2_notif_weekdays', 'Werktags', 'Weekdays') },
        { value: 'custom', icon: 'tune', label: strings.custom || tr('custom', 'Benutzerdefiniert', 'Custom') },
    ];

    // A transparent native <select> covers each tile / chip: a click anywhere opens the times.
    const renderTimeSelect = (value, onChange, label, options = timeOptions) => (
        <select className="ns-time-select" value={value || ''} onChange={(e) => onChange(e.target.value)} aria-label={label}>
            {options.map((option) => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    );

    const quietHours = (
        <section className="kj-settings-section ns-quiet">
            <div className="ns-quiet__caption">
                <h3 className="kj-settings-caption">{tr('pro_settings_notification_hours', 'Benachrichtigungszeiten', 'Notification hours')}</h3>
                {isInitialized && loading ? <span className="ns-quiet__saving">{strings.saving || tr('saving', 'Speichern...', 'Saving...')}</span> : null}
            </div>
            {!isInitialized ? (
                <div className="ns-quiet__tiles" aria-busy="true">
                    <span className="ns-bone" />
                    <span className="ns-bone" />
                </div>
            ) : (
                <>
                    {notificationType !== 'custom' ? (
                        <div className="ns-quiet__tiles">
                            <label className="ns-time-tile">
                                <span className="ns-time-tile__head">
                                    <span className="material-symbols-outlined" aria-hidden="true">wb_sunny</span>
                                    <span>{fromLabel}</span>
                                </span>
                                <span className="ns-time-tile__value">{startTime || '–'}</span>
                                {renderTimeSelect(startTime, (value) => handleTimeChange('startTime', value), fromLabel)}
                            </label>
                            <label className="ns-time-tile">
                                <span className="ns-time-tile__head">
                                    <span className="material-symbols-outlined" aria-hidden="true">bedtime</span>
                                    <span>{untilLabel}</span>
                                </span>
                                <span className="ns-time-tile__value">{endTime || '–'}</span>
                                {renderTimeSelect(endTime, (value) => handleTimeChange('endTime', value), untilLabel, endTimeOptions)}
                            </label>
                        </div>
                    ) : null}
                    <div className="ns-segmented" role="tablist" aria-label={tr('pro_settings_notification_hours', 'Benachrichtigungszeiten', 'Notification hours')}>
                        {scheduleTypes.map((type) => (
                            <button
                                key={type.value}
                                type="button"
                                role="tab"
                                aria-selected={notificationType === type.value}
                                className={`kj-tab ns-segmented__tab${notificationType === type.value ? ' is-active' : ''}`}
                                onClick={() => setNotificationType(type.value)}
                            >
                                <span className="material-symbols-outlined" aria-hidden="true">{type.icon}</span>
                                <span>{type.label}</span>
                            </button>
                        ))}
                    </div>
                    {notificationType === 'custom' ? (
                        <div className="kj-settings-card">
                            {DAYS_OF_WEEK.map((day) => {
                                const pref = customPreferences[day.key];
                                const dayLabel = getDayLabel(day.key);
                                return (
                                    <SettingsRow
                                        key={day.key}
                                        className={`ns-day${pref ? ' is-on' : ''}`}
                                        icon={pref ? 'calendar_today' : 'bedtime'}
                                        label={dayLabel}
                                        sub={pref ? undefined : tr('pro_settings_quiet_all_day', 'Ganztägig Ruhe', 'Quiet all day')}
                                        toggle={{ checked: !!pref, onChange: () => toggleDayEnabled(day.key) }}
                                        expanded={!!pref}
                                    >
                                        {pref ? (
                                            <div className="ns-day__times">
                                                <label className="ns-time-chip">
                                                    <span className="ns-time-chip__label">{fromLabel}</span>
                                                    <span className="ns-time-chip__value">{pref.startTime || '–'}</span>
                                                    {renderTimeSelect(pref.startTime, (value) => updateCustomPreference(day.key, 'startTime', value), `${dayLabel}: ${fromLabel}`)}
                                                </label>
                                                <label className="ns-time-chip">
                                                    <span className="ns-time-chip__label">{untilLabel}</span>
                                                    <span className="ns-time-chip__value">{pref.endTime || '–'}</span>
                                                    {renderTimeSelect(pref.endTime, (value) => updateCustomPreference(day.key, 'endTime', value), `${dayLabel}: ${untilLabel}`, endTimeOptions)}
                                                </label>
                                            </div>
                                        ) : null}
                                    </SettingsRow>
                                );
                            })}
                        </div>
                    ) : null}
                </>
            )}
        </section>
    );

    return (
        <div className="notification-settings ns-pro">
            {permissionBanner}
            <SettingsSection caption={tr('pro_settings_push', 'Push', 'Push')}>
                <SettingsRow
                    icon="desktop_windows"
                    label={t('browser_notifications', 'Desktop notifications')}
                    sub={desktopSub}
                    busy={pushBusy}
                    toggle={{
                        checked: desktopOn,
                        onChange: (next) => setDesktopNotifications(next),
                        disabled: !pushSupported || pushBusy || pushPermission === 'denied' || pushPermission === 'unsupported',
                    }}
                />
                {typeof onToggleTodoNotification === 'function' ? (
                    <SettingsRow
                        icon="list_alt_check"
                        label={t('todo_notification_label', 'ToDo notifications')}
                        sub={t('todo_notification_info', "You'll be notified whenever a new ToDo is assigned to you.")}
                        toggle={{ checked: !!todoNotificationEnabled, onChange: () => onToggleTodoNotification() }}
                    />
                ) : null}
                {typeof onToggleEmailPushNotification === 'function' ? (
                    <SettingsRow
                        icon="mail"
                        label={t('email_push_notification_label', 'Push notifications for new emails')}
                        sub={
                            emailPushLocked
                                ? t('email_push_notification_io_only', 'Only available with Kjuup.io – e-mail integration is part of the Kjuup.io plan.')
                                : t('email_push_notification_info', "You'll be notified when a new email arrives in your inbox.")
                        }
                        dimmed={emailPushLocked}
                        toggle={{
                            checked: !emailPushLocked && !!emailPushNotificationEnabled,
                            disabled: emailPushLocked,
                            onChange: () => onToggleEmailPushNotification(),
                        }}
                    />
                ) : null}
            </SettingsSection>
            {emailSection}
            {quietHours}
        </div>
    );
}

export default NotificationSettings; 